import { useSuccessFeeTermsQuery } from '@/hooks/queries/use-success-fee-terms';
import { useSuccessFeeLockStore } from '@/stores/success-fee-lock-store';
import type { SuccessFeeTermsView } from '@fluenceur/types';
import { useEffect } from 'react';

export interface SuccessFeeLock {
  isLocked: boolean;
  view: SuccessFeeTermsView | undefined;
  /** The instant read off a refused request, before the view has answered. */
  fallbackDeadline: string | null;
}

/**
 * Whether the dashboard is closed for this organization, from both signals.
 *
 * The terms endpoint is the authority: it answers even while locked, and its
 * word decides. But it is read on a stale window, so a session that was open
 * when the deadline passed learns of the lock from the first refused request
 * instead, and swaps to the locked page at once rather than watching every
 * panel fail.
 *
 * That refusal only stands until the endpoint answers again, which is why the
 * refetch below is fired on it: an answer newer than the refusal replaces it,
 * in both directions.
 */
export function useSuccessFeeLock(): SuccessFeeLock {
  const { data: view, dataUpdatedAt, refetch } = useSuccessFeeTermsQuery();
  const lockedAt = useSuccessFeeLockStore((store) => store.lockedAt);
  const fallbackDeadline = useSuccessFeeLockStore((store) => store.deadline);
  const release = useSuccessFeeLockStore((store) => store.release);

  const answeredState = view?.state.state;
  const isAnswerNewerThanRefusal = lockedAt !== null && dataUpdatedAt > lockedAt;

  useEffect(() => {
    if (lockedAt === null) return;

    refetch();
  }, [lockedAt, refetch]);

  useEffect(() => {
    if (isAnswerNewerThanRefusal && answeredState !== 'locked') release();
  }, [isAnswerNewerThanRefusal, answeredState, release]);

  const refusalStands = lockedAt !== null && !isAnswerNewerThanRefusal;

  return {
    isLocked: answeredState === 'locked' || refusalStands,
    view,
    fallbackDeadline,
  };
}
